var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown } from '@fortawesome/pro-solid-svg-icons/faSortDown';
import { faSortUp } from '@fortawesome/pro-solid-svg-icons/faSortUp';
import { faSort } from '@fortawesome/pro-solid-svg-icons/faSort';
import { useTable, useFilters, useGlobalFilter, usePagination, useSortBy, } from 'react-table';
import { faEllipsisH } from '@fortawesome/pro-solid-svg-icons';
import { usePrevious } from '../../utils/hooks/values';
import Pagination from '../Pagination';
import { P } from '../UI-helpers/typography';
import Grid from './Grid';
import * as sortTypes from './sortTypes';
import { Main, TableBodyContainer, TableHeaderRow, TableHeaderTh, TableContainer, TableBodyRow, TableBodyData, EmptyTableRow, EmptyTableRowText, EmptyTableContainer, } from './styles';
var DEFAULT_PAGE_SIZE = 10;
var DEFAULT_PAGE_INDEX = 0;
var Table = function (props) {
    var _a;
    var firstLoadRef = useRef(true);
    function DefaultColumnFilter(_a) {
        var _b = _a.column, filterValue = _b.filterValue, preFilteredRows = _b.preFilteredRows, setFilter = _b.setFilter;
        var count = preFilteredRows.length;
        return (_jsx("input", { value: filterValue || '', onChange: function (e) {
                setFilter(e.target.value || undefined); // Set undefined to remove the filter entirely
            }, placeholder: "Search ".concat(count, " records...") }));
    }
    var clickable = !!props.onRowClick;
    var defaultColumn = React.useMemo(function () { return ({
        // Let's set up our default Filter UI
        Filter: DefaultColumnFilter,
    }); }, []);
    var identifier = React.useMemo(function () { return function (row) {
        return props.rowId && row[props.rowId] !== undefined ? row[props.rowId] : row.id;
    }; }, [props.rowId]);
    var caseInsensitiveSearch = React.useCallback(function (rowA, rowB, columnId) {
        return sortTypes.alphanumeric(rowA, rowB, columnId);
    }, []);
    var sortTypesProp = React.useMemo(function () { return ({
        alphanumeric: caseInsensitiveSearch,
    }); }, [caseInsensitiveSearch]);
    var safePageSizeProp = props.remotePagination
        ? ((_a = props.remotePaginationConfig) === null || _a === void 0 ? void 0 : _a.pageSize) || DEFAULT_PAGE_INDEX
        : props.pageSize || DEFAULT_PAGE_SIZE;
    var safeInitialPageIndex = props.pageIndex || DEFAULT_PAGE_INDEX;
    var initialStateRef = useRef({
        pageSize: safePageSizeProp,
        pageIndex: safeInitialPageIndex,
    });
    var _b = useTable({
        columns: props.columns,
        data: props.data,
        defaultColumn: defaultColumn,
        initialState: initialStateRef.current,
        getRowId: identifier,
        autoResetPage: false,
        autoResetSortBy: false,
        autoResetGlobalFilters: false,
        sortTypes: sortTypesProp,
        disableMultiSort: true,
    }, useFilters, useGlobalFilter, useSortBy, usePagination), getTableProps = _b.getTableProps, getTableBodyProps = _b.getTableBodyProps, headerGroups = _b.headerGroups, page = _b.page, gotoPage = _b.gotoPage, pageCount = _b.pageCount, setPageSize = _b.setPageSize, setGlobalFilter = _b.setGlobalFilter, _c = _b.state, pageIndex = _c.pageIndex, pageSize = _c.pageSize, prepareRow = _b.prepareRow;
    var prevSearch = usePrevious(props.searchValue);
    // const prevIndex = usePrevious(pageIndex);
    useEffect(function () {
        var _a;
        if (firstLoadRef.current && ((_a = props === null || props === void 0 ? void 0 : props.data) === null || _a === void 0 ? void 0 : _a.length)) {
            // run only on first load
            firstLoadRef.current = false;
            // only set page if it doesn't exceed the page count, otherwise set to max page
            gotoPage(safeInitialPageIndex > pageCount - 1
                ? pageCount - 1
                : safeInitialPageIndex);
        }
    }, [
        gotoPage,
        props.gotoPage,
        props.data,
        pageCount,
        props,
        safeInitialPageIndex,
    ]);
    useEffect(function () {
        if (prevSearch !== props.searchValue) {
            setGlobalFilter(props.searchValue);
            if (!firstLoadRef.current &&
                pageIndex !== 0 &&
                props.searchValue &&
                props.searchValue !== '') {
                // only reset page after first load and search is not empty
                gotoPage(0);
            }
        }
    }, [setGlobalFilter, prevSearch, props.searchValue, gotoPage, pageIndex]);
    var EmptyTable = (_jsxs(EmptyTableContainer, { children: [_jsx(FontAwesomeIcon, { icon: props.defaultIcon || faEllipsisH, style: { marginBottom: 3 }, size: "3x", color: "#848484", fixedWidth: true }), props.defaultText && _jsx(P, __assign({ fontSize: 16 }, { children: props.defaultText }))] }));
    return (_jsxs(Main, __assign({ grid: props.grid, heightMax: props.heightMax, noBorder: props.noBorder }, { children: [!props.grid && (_jsx(TableBodyContainer, { children: _jsxs(TableContainer, __assign({}, getTableProps(), { onClick: function (e) {
                        if (props.closeContextMenu)
                            props.closeContextMenu(e);
                    } }, { children: [_jsx("thead", { children: headerGroups.map(function (headerGroup, index) { return (_jsx(TableHeaderRow, __assign({ disableElevation: true }, headerGroup.getHeaderGroupProps(), { children: headerGroup.headers.map(function (column, i) {
                                    var _a, _b, _c, _d, _e, _f;
                                    return (_jsx(TableHeaderTh, __assign({ onClick: function (_) {
                                            var _a, _b;
                                            if ((props === null || props === void 0 ? void 0 : props.remotePagination) &&
                                                ((_a = props === null || props === void 0 ? void 0 : props.remotePaginationConfig) === null || _a === void 0 ? void 0 : _a.onSort)) {
                                                (_b = props === null || props === void 0 ? void 0 : props.remotePaginationConfig) === null || _b === void 0 ? void 0 : _b.onSort(column.id);
                                            }
                                        } }, (!props.remotePagination
                                        ? column.getHeaderProps(column.getSortByToggleProps())
                                        : null), column, { children: _jsxs("span", __assign({ className: "".concat(!column.wrapContent &&
                                                "flex flex-row justify-".concat(column.textAlign ? column.textAlign : 'start')) }, { children: [column.render('Header'), !column.disableSortBy &&
                                                    column.Header &&
                                                    column.Header !== 'Action' &&
                                                    // eslint-disable-next-line no-nested-ternary
                                                    (column.isSorted ||
                                                        (props.remotePagination &&
                                                            ((_b = (_a = props.remotePaginationConfig) === null || _a === void 0 ? void 0 : _a.sortConfig) === null || _b === void 0 ? void 0 : _b.order) &&
                                                            ((_d = (_c = props.remotePaginationConfig) === null || _c === void 0 ? void 0 : _c.sortConfig) === null || _d === void 0 ? void 0 : _d.field) ===
                                                                column.id) ? (column.isSortedDesc ||
                                                        (props.remotePagination &&
                                                            ((_f = (_e = props.remotePaginationConfig) === null || _e === void 0 ? void 0 : _e.sortConfig) === null || _f === void 0 ? void 0 : _f.order) === 'desc') ? (_jsx("div", __assign({ style: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        } }, { children: _jsx(FontAwesomeIcon, { icon: faSortDown, width: 16, height: 16, color: "#000000", fixedWidth: true }) }))) : (_jsx("div", __assign({ style: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        } }, { children: _jsx(FontAwesomeIcon, { icon: faSortUp, width: 16, height: 16, color: "#000000", fixedWidth: true }) })))) : (_jsx("div", __assign({ style: {
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        } }, { children: _jsx(FontAwesomeIcon, { icon: faSort, width: 16, height: 16, color: "#000000", fixedWidth: true }) }))))] })) }), column.id || i));
                                }) }), (headerGroup === null || headerGroup === void 0 ? void 0 : headerGroup.id) || index)); }) }), _jsxs("tbody", __assign({}, getTableBodyProps(), { children: [page.map(function (row) {
                                    prepareRow(row);
                                    return (_jsx(TableBodyRow, __assign({ clickable: clickable, onContextMenu: function (e) {
                                            if (props.openContextMenu)
                                                props.openContextMenu(e, row);
                                        } }, row.getRowProps(), { onClick: function (e) {
                                            if (props.onRowClick)
                                                props.onRowClick(row.original);
                                            if (props.closeContextMenu)
                                                props.closeContextMenu(e);
                                        }, darkBorder: row.original.darkBorder }, { children: row.cells.map(function (cell, index) { return (_jsx(TableBodyData
                                        // eslint-disable-next-line react/no-array-index-key
                                        , __assign({}, cell, { children: cell.render('Cell') }), "".concat(identifier(row), "-").concat(index))); }) }), identifier(row)));
                                }), page.length <= 0 && (_jsx(TableBodyRow, { children: _jsx("td", __assign({ colSpan: 10000 }, { children: _jsx(EmptyTableRow, { children: _jsx(EmptyTableRowText, { children: "Empty Item List" }) }) })) }))] }))] })) })), props.grid && props.renderGridItem && page.length > 0 && (_jsx(_Fragment, { children: _jsx(Grid
                // appsGrid={props.apps}
                , { 
                    // appsGrid={props.apps}
                    data: page, rowId: props.rowId, renderGridItem: props.renderGridItem, gridMinWidth: props.gridMinWidth }) })), props.grid && page.length === 0 && EmptyTable, !props.hidePagination && !props.remotePagination && page.length > 0 && (_jsx(Pagination, { pageCount: pageCount, onPageChange: function (index) {
                    // Notify parent of page change
                    if (props.gotoPage)
                        props.gotoPage(index);
                    gotoPage(index);
                }, selectedPage: pageIndex + 1, selectionValueList: ['10', '20', '30'], selectionValueSuffix: "/page", defaultSelectionListValue: String(pageSize), onListValueChange: function (value) { return setPageSize(Number(value)); }, hideBorders: props.grid })), !props.hidePagination &&
                props.remotePagination &&
                props.remotePaginationConfig &&
                page.length > 0 && (_jsx(Pagination, { pageCount: props.remotePaginationConfig.pageCount, onPageChange: function (index) {
                    var _a;
                    if ((_a = props.remotePaginationConfig) === null || _a === void 0 ? void 0 : _a.onPageChange)
                        props.remotePaginationConfig.onPageChange(index);
                }, selectedPage: props.remotePaginationConfig.pageIndex, selectionValueList: ['10', '20', '30'], selectionValueSuffix: "/page", defaultSelectionListValue: String(props.remotePaginationConfig.pageSize), onListValueChange: function (value) {
                    var _a;
                    setPageSize(Number(value));
                    (_a = props.remotePaginationConfig) === null || _a === void 0 ? void 0 : _a.onPageSizeChange(Number(value));
                }, hideBorders: props.grid }))] })));
};
export default Table;
